import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type {
  ChatRoomEntryResponse,
  ChatRoomResponse,
  chatRoomMemberResponse,
  PaginatedResponse,
  User,
} from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

export const chatsApi = {
  getChatRooms: async (page = 1, perPage = 10): Promise<PaginatedResponse<ChatRoomResponse>> => {
    try {
      const response = await api.get<PaginatedResponse<ChatRoomResponse>>(`${API_URLS.CHAT}`, {
        params: {
          page,
          limit: perPage,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getChatRoomEntries: async (
    id: string | number,
    page = 1,
    perPage = 10,
  ): Promise<PaginatedResponse<ChatRoomEntryResponse>> => {
    try {
      const response = await api.get<PaginatedResponse<ChatRoomEntryResponse>>(
        `${API_URLS.CHAT}/${id}/entries-with-user-short`,
        {
          params: {
            page,
            limit: perPage,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getChatRoom: async (id: string | number): Promise<ChatRoomResponse> => {
    try {
      const response = await api.get<ChatRoomResponse>(`${API_URLS.CHAT}/${id}`);
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getChatRoomMembers: async (id: string | number): Promise<chatRoomMemberResponse> => {
    try {
      const response = await api.get<chatRoomMemberResponse>(`${API_URLS.CHAT}/${id}/users`);
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  createChatRoomEntry: async (
    id: string | number,
    data: FormData | string,
  ): Promise<ChatRoomEntryResponse> => {
    try {
      let response: { data: ChatRoomEntryResponse };

      if (data instanceof FormData) {
        // FormDataの場合はmultipart/form-dataとして送信
        // APIの要求に合わせてキー名を設定
        response = await api.post(`${API_URLS.CHAT}/${id}/entry`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // 文字列の場合はFormDataに変換して送信
        const formData = new FormData();
        formData.append("ReplyForm[message]", data);

        response = await api.post(`${API_URLS.CHAT}/${id}/entry`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  createChatRoom: async (
    title: string,
    message: string,
    recipients: string[],
  ): Promise<ChatRoomResponse> => {
    try {
      const response = await api.post<ChatRoomResponse>(API_URLS.CHAT, {
        title,
        message,
        recipient: recipients,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getMentionAvailableUsers: async (params?: {
    keyword?: string;
    messageId?: string | number;
  }): Promise<User[]> => {
    try {
      interface MentionUserResponse {
        results?: Array<{ user: User }>;
      }

      const response = await api.get<MentionUserResponse>(
        `${API_URLS.CHAT}/filter-mention-available-users`,
        {
          params,
        },
      );

      // APIレスポンスから直接User[]を抽出
      return response.data.results?.map((item) => item.user) || [];
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  addChatRoomMember: async (
    chatRoomId: string | number,
    userIds: string[] | number[],
  ): Promise<void> => {
    try {
      await api.post(`${API_URLS.CHAT}/${chatRoomId}/user/add-multiple`, {
        userIds,
      });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  deleteChatRoomEntry: async (
    chatRoomId: string | number,
    entryId: string | number,
  ): Promise<void> => {
    try {
      await api.delete(`${API_URLS.CHAT}/${chatRoomId}/entry/${entryId}`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  leaveChatRoom: async (chatRoomId: string | number): Promise<void> => {
    try {
      await api.delete(`${API_URLS.CHAT}/${chatRoomId}/user/leave-current-user`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  visitChatRoom: async (chatRoomId: string | number): Promise<void> => {
    try {
      await api.put(`${API_URLS.CHAT}/${chatRoomId}`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  searchEntries: async (params: {
    keyword: string;
    messageIds?: string[];
  }): Promise<PaginatedResponse<ChatRoomEntryResponse>> => {
    try {
      // messageIdsがある場合はカンマ区切りの文字列に変換
      const formattedParams: { keyword: string; messageIds?: string | string[] } = { ...params };
      if (
        formattedParams.messageIds &&
        Array.isArray(formattedParams.messageIds) &&
        formattedParams.messageIds.length > 0
      ) {
        formattedParams.messageIds = formattedParams.messageIds.join(",");
      }

      const response = await api.get<PaginatedResponse<ChatRoomEntryResponse>>(
        `${API_URLS.CHAT}/entry/search-with-keyword`,
        {
          params: formattedParams,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
  searchChatRooms: async (params: {
    keyword: string;
    messageIds?: string[];
  }): Promise<PaginatedResponse<ChatRoomResponse>> => {
    try {
      // messageIdsがある場合はカンマ区切りの文字列に変換
      const formattedParams: { keyword: string; messageIds?: string | string[] } = { ...params };
      if (
        formattedParams.messageIds &&
        Array.isArray(formattedParams.messageIds) &&
        formattedParams.messageIds.length > 0
      ) {
        formattedParams.messageIds = formattedParams.messageIds.join(",");
      }

      const response = await api.get<PaginatedResponse<ChatRoomResponse>>(
        `${API_URLS.CHAT}/search-with-keyword`,
        {
          params: formattedParams,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
};
